// MarketStatusBadge — shows reps which system is currently driving the market
// pricing adjustment on a quote, and prompts them to run Market Intelligence
// when there's no analysis yet.
//
// Added v3.5.37 as part of the market-tier vs market-intelligence clarity work.
// In v3.5.37 the underlying engine math is unchanged — this is purely UI clarity.
// In v3.5.38 (planned), tier multipliers will be deactivated; this badge will
// then become the only source of "why is the price adjusted by X%?" info on screen.
//
// Three primary states:
//   1. MI active and fresh — purple/blue info box: "Pricing driven by Market Intel"
//      with the percentage adjustment, market label, and last-analyzed date
//   2. MI active but stale (>3mo) — amber warning: "Analysis is X months old"
//      with a Re-Analyze CTA
//   3. MI absent — neutral muted box explaining the tier baseline is active,
//      with a prominent "Run Market Intelligence" CTA
//
// Props:
//   aiMultiplier         — number | null. The MI-derived multiplier (e.g., 0.92 for -8% adjustment)
//   aiMultiplierTier     — string | null. The MI label ("Premium" / "Standard" / etc.)
//   selectedMkt          — { name, tier_key, multiplier } | null. The static admin tier currently selected
//   marketAnalysis       — full analysis object from market_rate_analyses, if loaded
//   marketCity           — string | null. Current quote's market city
//   marketState          — string | null. Current quote's market state
//   onRunAnalysis        — function. Triggered when rep clicks "Run Market Intelligence"
//   analyzeLoading       — boolean. When true, the CTA shows a spinner / disabled
//   compact              — boolean. Default false; renders smaller for FlexIT-style sidebars
//   locked               — boolean. When true (locked quote), badge is purely informational

import React from 'react';
import { isStale, staticTierToMILabel } from '../lib/marketRates';

// (staticTierToMILabel is exported from marketRates.js as of v3.5.37 so other
// modules can use it directly for tier dropdowns and quote summaries.)

export default function MarketStatusBadge({
  aiMultiplier,
  aiMultiplierTier,
  selectedMkt,
  marketAnalysis,
  marketCity,
  marketState,
  onRunAnalysis,
  analyzeLoading = false,
  compact = false,
  locked = false,
}) {
  const hasMI    = aiMultiplier != null && Number.isFinite(aiMultiplier);
  const analyzedAt = marketAnalysis?.analyzed_at || null;
  const stale    = analyzedAt ? isStale(analyzedAt) : false;
  const adjustmentPct = hasMI ? (aiMultiplier - 1) * 100 : 0;
  const adjustmentLabel = hasMI
    ? (Math.abs(adjustmentPct) < 0.05 ? '0.0%' : `${adjustmentPct > 0 ? '+' : ''}${adjustmentPct.toFixed(1)}%`)
    : null;

  // Days since last analysis for the "X days ago" display
  const daysAgo = analyzedAt
    ? Math.floor((Date.now() - new Date(analyzedAt).getTime()) / (1000 * 60 * 60 * 24))
    : null;

  // ── State 1+2: MI is active ──────────────────────────────────────────────
  if (hasMI) {
    const bg     = stale ? '#fffbeb' : '#f5f3ff';
    const border = stale ? '#fde68a' : '#c4b5fd';
    const color  = stale ? '#92400e' : '#5b21b6';
    const icon   = stale ? '⚠' : '🛰';
    return (
      <div style={{
        background: bg, border: `1px solid ${border}`, borderRadius: 5,
        padding: compact ? '6px 8px' : '8px 10px', fontSize: compact ? 9 : 10,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color, fontWeight: 600 }}>
          <span style={{ fontSize: compact ? 10 : 12 }}>{icon}</span>
          <span style={{ flex: 1 }}>
            Pricing driven by Market Intelligence:{' '}
            <strong>{aiMultiplierTier || 'Standard'}</strong> market,{' '}
            <strong style={{ fontFamily: 'DM Mono, monospace' }}>{adjustmentLabel}</strong> adjustment
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 4, color: stale ? '#78350f' : '#7c3aed', fontSize: compact ? 8 : 9 }}>
          <span>
            {marketCity || marketAnalysis?.city}
            {(marketState || marketAnalysis?.state) ? `, ${marketState || marketAnalysis.state}` : ''}
            {daysAgo != null && (
              <> · analyzed {daysAgo === 0 ? 'today' : daysAgo === 1 ? 'yesterday' : `${daysAgo} days ago`}</>
            )}
            {stale && <span style={{ marginLeft: 4, fontWeight: 700 }}>(stale)</span>}
          </span>
          {!locked && onRunAnalysis && (
            <button onClick={onRunAnalysis} disabled={analyzeLoading}
              style={{
                marginLeft: 'auto',
                padding: '2px 8px', background: 'white',
                border: `1px solid ${stale ? '#fde68a' : '#c4b5fd'}`,
                borderRadius: 3, fontSize: compact ? 8 : 9, color,
                fontWeight: 600,
                cursor: analyzeLoading ? 'not-allowed' : 'pointer',
                opacity: analyzeLoading ? 0.6 : 1,
              }}>
              {analyzeLoading ? 'Analyzing…' : (stale ? 'Re-analyze' : 'Refresh')}
            </button>
          )}
        </div>
        {selectedMkt && (
          <div style={{ marginTop: 4, fontSize: compact ? 7 : 8, color: '#9ca3af', fontStyle: 'italic' }}>
            Tier classification only: {staticTierToMILabel(selectedMkt.tier_key)} ({selectedMkt.name}). Pricing comes from Market Intelligence above.
          </div>
        )}
      </div>
    );
  }

  // ── State 3: MI absent ───────────────────────────────────────────────────
  // Show the static-tier baseline that's currently being applied. The empty
  // state intentionally does NOT include a "Run Market Intelligence" button —
  // each quote page already has a MarketRateCard (or equivalent) that owns the
  // analyze workflow. Adding a second button here would create two competing
  // entry points to the same action. Instead, the badge points reps to the
  // existing UI when relevant.
  const tierMult = parseFloat(selectedMkt?.multiplier);
  const tierMultLabel = Number.isFinite(tierMult) && tierMult !== 1
    ? `${tierMult.toFixed(2)}× (${tierMult > 1 ? '+' : ''}${((tierMult - 1) * 100).toFixed(0)}%)`
    : '1.00× (base rates)';

  return (
    <div style={{
      background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 5,
      padding: compact ? '6px 8px' : '8px 10px', fontSize: compact ? 9 : 10,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#6b7280' }}>
        <span style={{ fontSize: compact ? 10 : 12 }}>ⓘ</span>
        <span style={{ flex: 1 }}>
          Tier baseline: <strong style={{ fontFamily: 'DM Mono, monospace', color: '#374151' }}>{tierMultLabel}</strong>
          {selectedMkt && (
            <> applied to package rates (<strong>{staticTierToMILabel(selectedMkt.tier_key)}</strong> / {selectedMkt.name})</>
          )}
        </span>
      </div>
      {!locked && (marketCity || marketState) && (
        <div style={{ marginTop: 4, fontSize: compact ? 8 : 9, color: '#7c3aed', fontStyle: 'italic' }}>
          🛰 Run Market Intelligence below for sharper pricing on {marketCity}{marketState ? `, ${marketState}` : ''}
        </div>
      )}
      {!locked && !marketCity && !marketState && (
        <div style={{ marginTop: 4, fontSize: compact ? 8 : 9, color: '#9ca3af', fontStyle: 'italic' }}>
          Enter a ZIP code above to enable Market Intelligence analysis.
        </div>
      )}
    </div>
  );
}
